import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store from '../state';
import maplibregl from 'maplibre-gl';

export class AvalancheLayer extends BaseProduct {
  public readonly id: ProductId = 'avalanche';
  private pinnedPopup: maplibregl.Popup | null = null;
  private hoverPopup: maplibregl.Popup | null = null;
  private mapEventsBound: boolean = false;
  private validAsOfTimestamp: string | null = null;

  protected onActivate(): void {
    this.refresh();
    this.bindMapEvents();
  }

  protected onDeactivate(): void {
    this.removeLayers();
    this.unbindMapEvents();
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  public refresh() {
    if (!this.active) return;
    this.setupLayers();
    this.updateVisibility();
  }

  private setupLayers() {
    const state = store.getState();
    const manifest = state.manifest;
    let apiServer = manifest?.api_server;
    if (apiServer === undefined || apiServer === null) {
      apiServer = "https://auth.skyskout.com/";
    } else if (apiServer === "") {
      const path = window.location.pathname;
      const dir = path.substring(0, path.lastIndexOf('/') + 1);
      apiServer = window.location.origin + dir;
    }
    if (!apiServer.endsWith('/')) apiServer += '/';
    const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const versionParam = manifest?.generated_at ? `?v=${encodeURIComponent(manifest.generated_at)}` : `?t=${Date.now()}`;
    const dataUrl = `${apiServer}${tileRoot}/observed/avalanche/avalanche.geojson${versionParam}`;

    // Fetch root metadata (valid_as_of timestamp)
    fetch(dataUrl)
      .then(async res => {
        if (!res.ok) return;
        const text = await res.text();
        const match = text.match(/"valid_as_of"\s*:\s*"([^"]+)"/);
        if (match) {
          this.validAsOfTimestamp = match[1];
        }
      })
      .catch(err => {
        console.warn('⚠️ Could not fetch avalanche valid_as_of metadata:', err);
      });

    // Add or update Avalanche GeoJSON Source
    if (!this.map.getSource('avalanche-source')) {
      this.map.addSource('avalanche-source', {
        type: 'geojson',
        data: dataUrl
      });
    } else {
      const source = this.map.getSource('avalanche-source') as maplibregl.GeoJSONSource;
      if (source && typeof source.setData === 'function') {
        source.setData(dataUrl);
      }
    }

    // Dynamic layer ordering: insert dynamic overlays beneath city labels
    const layers = this.map.getStyle()?.layers;
    let beforeId: string | undefined = undefined;
    if (layers) {
      const firstSymbol = layers.find(l => l.type === 'symbol');
      if (firstSymbol) beforeId = firstSymbol.id;
    }

    // 1. Fill Layer: Color matched by NAC danger level (1-5, -1/0 off-season)
    if (!this.map.getLayer('avalanche-fill')) {
      this.map.addLayer({
        id: 'avalanche-fill',
        type: 'fill',
        source: 'avalanche-source',
        paint: {
          'fill-color': [
            'match',
            ['get', 'danger_level'],
            1, '#50b848', // 1 - Low (Green)
            2, '#fff100', // 2 - Moderate (Yellow)
            3, '#f7941e', // 3 - Considerable (Orange)
            4, '#ed1c24', // 4 - High (Red)
            5, '#231f20', // 5 - Extreme (Black)
            /* fallback / -1 / 0 */ '#9ca3af' // Off-season (Neutral Gray)
          ],
          'fill-opacity': [
            'match',
            ['get', 'danger_level'],
            1, 0.35,
            2, 0.42,
            3, 0.52,
            4, 0.62,
            5, 0.72,
            /* fallback / -1 / 0 */ 0.16
          ]
        }
      }, beforeId);
    }

    // 2. Stroke Background (White outline halo)
    if (!this.map.getLayer('avalanche-stroke-bg')) {
      this.map.addLayer({
        id: 'avalanche-stroke-bg',
        type: 'line',
        source: 'avalanche-source',
        paint: {
          'line-color': '#ffffff',
          'line-width': ['interpolate', ['linear'], ['zoom'], 3, 2.0, 8, 3.5, 14, 5.0],
          'line-opacity': 0.65
        }
      }, beforeId);
    }

    // 3. Stroke Foreground (Danger colored border)
    if (!this.map.getLayer('avalanche-stroke-fg')) {
      this.map.addLayer({
        id: 'avalanche-stroke-fg',
        type: 'line',
        source: 'avalanche-source',
        paint: {
          'line-color': [
            'match',
            ['get', 'danger_level'],
            1, '#50b848',
            2, '#d97706',
            3, '#ea580c',
            4, '#dc2626',
            5, '#000000',
            /* fallback */ '#6b7280'
          ],
          'line-width': ['interpolate', ['linear'], ['zoom'], 3, 1.2, 8, 2.2, 14, 3.5]
        }
      }, beforeId);
    }

    // 4. Symbol Layer: Zone name + Danger rating badge
    if (!this.map.getLayer('avalanche-label')) {
      this.map.addLayer({
        id: 'avalanche-label',
        type: 'symbol',
        source: 'avalanche-source',
        minzoom: 6,
        layout: {
          'text-field': ['get', 'name'],
          'text-font': ['Noto Sans Regular'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 6, 11, 10, 13, 14, 15],
          'text-anchor': 'center'
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#000000',
          'text-halo-width': 2.0
        }
      }, beforeId);
    }
  }

  private updateVisibility() {
    const isVisible = true;
    ['avalanche-fill', 'avalanche-stroke-bg', 'avalanche-stroke-fg', 'avalanche-label'].forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        this.map.setLayoutProperty(layerId, 'visibility', isVisible ? 'visible' : 'none');
      }
    });
  }

  private removeLayers() {
    ['avalanche-label', 'avalanche-stroke-fg', 'avalanche-stroke-bg', 'avalanche-fill'].forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId);
      }
    });
    if (this.map.getSource('avalanche-source')) {
      this.map.removeSource('avalanche-source');
    }
  }

  private bindMapEvents() {
    if (this.mapEventsBound) return;
    this.mapEventsBound = true;

    this.hoverPopup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'gw-avalanche-popup',
      maxWidth: '380px'
    });

    ['avalanche-fill'].forEach(layerId => {
      this.map.on('mouseenter', layerId, this.handleMouseEnter);
      this.map.on('mouseleave', layerId, this.handleMouseLeave);
      this.map.on('click', layerId, this.handleClick);
    });
  }

  private unbindMapEvents() {
    if (!this.mapEventsBound) return;
    this.mapEventsBound = false;

    ['avalanche-fill'].forEach(layerId => {
      this.map.off('mouseenter', layerId, this.handleMouseEnter);
      this.map.off('mouseleave', layerId, this.handleMouseLeave);
      this.map.off('click', layerId, this.handleClick);
    });

    if (this.hoverPopup) {
      this.hoverPopup.remove();
      this.hoverPopup = null;
    }
    if (this.pinnedPopup) {
      this.pinnedPopup.remove();
      this.pinnedPopup = null;
    }
  }

  private getPriorityFeature(features?: maplibregl.MapGeoJSONFeature[]): Record<string, any> {
    if (!features || features.length === 0) return {};
    const sorted = [...features].sort((a, b) => {
      const dangerA = Number(a.properties?.danger_level) || -1;
      const dangerB = Number(b.properties?.danger_level) || -1;
      // Higher danger level takes priority
      return dangerB - dangerA;
    });
    return sorted[0].properties || {};
  }

  private handleMouseEnter = (e: maplibregl.MapLayerMouseEvent) => {
    this.map.getCanvas().style.cursor = 'pointer';
    if (this.pinnedPopup && this.pinnedPopup.isOpen()) return;
    if (!e.features || e.features.length === 0 || !this.hoverPopup) return;

    const p = this.getPriorityFeature(e.features);
    const html = this.buildPopupContent(p, false);

    this.hoverPopup
      .setLngLat(e.lngLat)
      .setHTML(html)
      .addTo(this.map);
  };

  private handleMouseLeave = () => {
    this.map.getCanvas().style.cursor = '';
    if (this.hoverPopup) {
      this.hoverPopup.remove();
    }
  };

  private handleClick = (e: maplibregl.MapLayerMouseEvent) => {
    if (!e.features || e.features.length === 0) return;

    if (this.hoverPopup) {
      this.hoverPopup.remove();
    }

    if (this.pinnedPopup) {
      this.pinnedPopup.remove();
    }

    const p = this.getPriorityFeature(e.features);
    const html = this.buildPopupContent(p, true);

    this.pinnedPopup = new maplibregl.Popup({
      closeButton: true,
      closeOnClick: true,
      closeOnMove: false,
      className: 'gw-avalanche-popup gw-avalanche-pinned',
      maxWidth: '400px'
    })
      .setLngLat(e.lngLat)
      .setHTML(html)
      .addTo(this.map);
  };

  private buildPopupContent(p: Record<string, any>, _isPinned: boolean = false): string {
    const dangerLevel = Number(p.danger_level) !== undefined && !isNaN(Number(p.danger_level)) ? Number(p.danger_level) : -1;
    const dangerTitle = p.danger_title || (dangerLevel === -1 ? 'No Rating / Off-Season' : `Level ${dangerLevel}`);
    const zoneName = p.name || 'Avalanche Forecast Zone';
    const center = p.center || 'Avalanche Center';
    const country = p.country || (center.toLowerCase().includes('canada') || center.toLowerCase().includes('parks') || center.toLowerCase().includes('québec') || center.toLowerCase().includes('quebec') ? 'CA' : 'US');
    const link = p.link || p.center_link || (country === 'CA' ? 'https://avalanche.ca' : 'https://avalanche.org');
    const travelAdvice = p.travel_advice || '';
    const highlights = p.highlights || '';
    const hasWarning = p.has_warning === true || (p.warning && p.warning.product);
    const warningProduct = p.warning_product || (p.warning ? p.warning.product : null);
    const offSeason = p.off_season === true || dangerLevel === -1;
    const elevationRatings = p.elevation_ratings;
    const problems = Array.isArray(p.problems) ? p.problems : [];

    let badgeBg = '#9ca3af';
    let badgeText = '#ffffff';
    let badgeBorder = 'rgba(255,255,255,0.2)';

    switch (dangerLevel) {
      case 1:
        badgeBg = '#50b848';
        badgeText = '#ffffff';
        break;
      case 2:
        badgeBg = '#fff100';
        badgeText = '#000000';
        break;
      case 3:
        badgeBg = '#f7941e';
        badgeText = '#ffffff';
        break;
      case 4:
        badgeBg = '#ed1c24';
        badgeText = '#ffffff';
        break;
      case 5:
        badgeBg = '#231f20';
        badgeText = '#ff4d4d';
        badgeBorder = '#dc2626';
        break;
      default:
        badgeBg = '#6b7280';
        badgeText = '#ffffff';
        break;
    }

    const agencyFlag = country === 'CA' ? '🇨🇦' : '🇺🇸';

    const warningBannerHtml = hasWarning ? `
      <div style="background: rgba(220, 38, 38, 0.2); border: 1px solid #dc2626; border-radius: 6px; padding: 6px 8px; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
        <span style="font-size: 1rem;">⚠️</span>
        <div>
          <div style="font-weight: 700; color: #ef4444; font-size: 0.76rem; text-transform: uppercase;">AVALANCHE WARNING ACTIVE</div>
          ${warningProduct ? `<div style="font-size: 0.68rem; color: #fca5a5;">${warningProduct}</div>` : ''}
        </div>
      </div>
    ` : '';

    // Elevation danger rating breakdown (Alpine, Treeline, Below Treeline)
    let elevationBreakdownHtml = '';
    if (elevationRatings && (elevationRatings.alp || elevationRatings.tln || elevationRatings.btl)) {
      const getElevBadge = (item?: { level: number; title: string; color: string }) => {
        if (!item || item.level === -1) return `<span style="color: var(--text-muted); font-size: 0.68rem;">Unrated</span>`;
        return `
          <span style="display: inline-flex; align-items: center; gap: 4px; font-weight: 700; font-size: 0.7rem; color: #fff;">
            <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${item.color}; border: 1px solid rgba(255,255,255,0.3);"></span>
            <span>${item.level} - ${item.title}</span>
          </span>
        `;
      };

      elevationBreakdownHtml = `
        <div style="background: rgba(0, 0, 0, 0.25); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 6px; padding: 6px 8px; margin: 6px 0;">
          <div style="font-size: 0.68rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Elevation Hazard Ratings:</div>
          <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; font-size: 0.72rem;">
            <div style="display: flex; flex-direction: column; gap: 1px;">
              <span style="color: var(--text-muted); font-size: 0.65rem;">Alpine</span>
              ${getElevBadge(elevationRatings.alp)}
            </div>
            <div style="display: flex; flex-direction: column; gap: 1px;">
              <span style="color: var(--text-muted); font-size: 0.65rem;">Treeline</span>
              ${getElevBadge(elevationRatings.tln)}
            </div>
            <div style="display: flex; flex-direction: column; gap: 1px;">
              <span style="color: var(--text-muted); font-size: 0.65rem;">Below Treeline</span>
              ${getElevBadge(elevationRatings.btl)}
            </div>
          </div>
        </div>
      `;
    }

    // Avalanche Problem chips
    let problemsHtml = '';
    if (problems.length > 0) {
      problemsHtml = `
        <div style="margin: 6px 0;">
          <div style="font-size: 0.68rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Active Avalanche Problems:</div>
          <div style="display: flex; flex-wrap: wrap; gap: 4px;">
            ${problems.map(prob => `
              <span style="display: inline-flex; align-items: center; gap: 3px; background: rgba(234, 88, 12, 0.15); border: 1px solid rgba(234, 88, 12, 0.4); color: #fb923c; padding: 2px 6px; border-radius: 4px; font-size: 0.68rem; font-weight: 600;">
                <span>⚠️ ${prob.name}</span>
                ${prob.likelihood ? `<span style="color: var(--text-muted); font-weight: 400;">(${prob.likelihood})</span>` : ''}
              </span>
            `).join('')}
          </div>
        </div>
      `;
    }

    const datesHtml = (p.start_date || p.end_date) ? `
      <div style="font-size: 0.68rem; color: var(--text-muted); margin-top: 4px;">
        Valid: ${p.start_date ? new Date(p.start_date).toLocaleDateString() : ''} ${p.end_date ? `- ${new Date(p.end_date).toLocaleDateString()}` : ''}
      </div>
    ` : '';

    return `
      <div style="font-family: inherit; color: var(--text-primary, #fff); font-size: 0.8rem; line-height: 1.4; padding: 2px;">
        ${warningBannerHtml}
        
        <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; margin-bottom: 6px;">
          <div>
            <div style="font-size: 0.92rem; font-weight: 700; color: var(--text-primary, #fff); line-height: 1.2;">
              🏔️ ${zoneName}
            </div>
            <div style="font-size: 0.72rem; color: var(--text-muted, #94a3b8); margin-top: 2px; display: flex; align-items: center; gap: 4px;">
              <span>${agencyFlag}</span>
              <span>${center}${p.state && p.state !== 'Canada' ? ` (${p.state})` : ''}</span>
            </div>
          </div>
          
          <div style="background: ${badgeBg}; color: ${badgeText}; border: 1px solid ${badgeBorder}; font-weight: 700; font-size: 0.72rem; padding: 3px 8px; border-radius: 4px; white-space: nowrap; flex-shrink: 0; text-transform: uppercase;">
            ${dangerLevel > 0 ? `Level ${dangerLevel}: ` : ''}${dangerTitle}
          </div>
        </div>

        ${elevationBreakdownHtml}
        ${problemsHtml}

        ${travelAdvice ? `
          <div style="background: rgba(255, 255, 255, 0.05); border-left: 3px solid ${badgeBg}; padding: 6px 8px; margin: 6px 0; border-radius: 0 4px 4px 0; font-size: 0.72rem; color: var(--text-secondary, #cbd5e1);">
            <div style="font-weight: 600; color: var(--text-primary, #fff); margin-bottom: 2px;">Travel Advice:</div>
            ${travelAdvice}
          </div>
        ` : (offSeason ? `
          <div style="background: rgba(255, 255, 255, 0.04); border-left: 3px solid #6b7280; padding: 6px 8px; margin: 6px 0; border-radius: 0 4px 4px 0; font-size: 0.72rem; color: var(--text-muted, #94a3b8);">
            This avalanche center is currently off-season or has not issued an active danger rating. Check direct center bulletins for spring/summer updates.
          </div>
        ` : '')}

        ${highlights ? `
          <div style="font-size: 0.7rem; color: var(--text-muted, #94a3b8); margin: 6px 0; font-style: italic; max-height: 80px; overflow-y: auto;">
            "${highlights}"
          </div>
        ` : ''}

        ${datesHtml}

        <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.1);">
          <a href="${link}" target="_blank" rel="noopener noreferrer" style="display: inline-flex; align-items: center; gap: 4px; background: rgba(56, 189, 248, 0.15); border: 1px solid rgba(56, 189, 248, 0.4); color: #38bdf8; padding: 4px 8px; border-radius: 4px; font-size: 0.72rem; font-weight: 600; text-decoration: none;">
            <span>${country === 'CA' ? 'Avalanche Canada Bulletin' : 'Official Center Bulletin'}</span>
            <span>↗</span>
          </a>

          ${this.validAsOfTimestamp ? `
            <span style="font-size: 0.62rem; color: var(--text-muted, #64748b);">
              Synced: ${new Date(this.validAsOfTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          ` : ''}
        </div>
      </div>
    `;
  }
}
